import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import t from "../translations";
import {
  ISOLATION_PARAMS,
  ISOLATION_PARAM_MIN_VALUE,
  type IsolationParamName,
  type WizardIsolationParams,
  clampIsolationParam,
} from "./clearanceRulesConfig";

type Props = {
  isolationParams: WizardIsolationParams;
  onChange: (isolationParams: WizardIsolationParams) => void;
};

const FIELD_TEXT: Record<
  IsolationParamName,
  { label: string; unit: string; hint: string }
> = {
  isolation_min_point_density: {
    label: t.IsolationMinPointDensityLabel,
    unit: t.IsolationMinPointDensityUnit,
    hint: t.IsolationMinPointDensityHint,
  },
  isolation_radius: {
    label: t.IsolationRadiusLabel,
    unit: t.IsolationRadiusUnit,
    hint: t.IsolationRadiusHint,
  },
  isolation_angle_tolerance_degrees: {
    label: t.IsolationAngleToleranceLabel,
    unit: t.IsolationAngleToleranceUnit,
    hint: t.IsolationAngleToleranceHint,
  },
};

const FIELD_WIDTH = 160;

function InfoTooltipContent() {
  return (
    <Stack spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="caption">{t.IsolationEmptyHint}</Typography>
      {ISOLATION_PARAMS.map(({ name }) => (
        <Typography key={name} variant="caption">
          <strong>{FIELD_TEXT[name].label}</strong>
          <br />
          {FIELD_TEXT[name].hint}
        </Typography>
      ))}
    </Stack>
  );
}

export default function IsolationParamsFields({
  isolationParams,
  onChange,
}: Props) {
  const setParam = (name: IsolationParamName, value: number | null) =>
    onChange({ ...isolationParams, [name]: value });

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <Typography variant="body2">{t.IsolationLabel}</Typography>
        <Tooltip title={<InfoTooltipContent />}>
          <InfoOutlinedIcon
            fontSize="small"
            color="action"
            aria-label={t.IsolationInfoLabel}
            tabIndex={0}
          />
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1}>
        {ISOLATION_PARAMS.map(({ name, defaultValue }) => {
          const value = isolationParams[name];
          const { label, unit } = FIELD_TEXT[name];
          return (
            <TextField
              key={name}
              size="small"
              type="number"
              label={label}
              placeholder={String(defaultValue)}
              value={value ?? ""}
              onChange={(event) =>
                setParam(
                  name,
                  event.target.value === "" ? null : Number(event.target.value)
                )
              }
              onBlur={() => {
                if (value !== null) setParam(name, clampIsolationParam(value));
              }}
              sx={{ width: FIELD_WIDTH }}
              slotProps={{
                htmlInput: { min: ISOLATION_PARAM_MIN_VALUE },
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{unit}</InputAdornment>
                  ),
                },
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
